import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Check } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import NotFound from "@/pages/NotFound";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";
import { SUBURBS } from "@/data/suburbs";
import { useSiteContent } from "@/context/ContentContext";

export default function LocationDetail() {
  const { suburb } = useParams();
  const { categories, suburbs } = useSiteContent();
  const loc = suburbs.find((s) => s.slug === suburb);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [suburb]);

  if (!loc) return <NotFound />;

  const title = `Tiling in ${loc.name} | Perth Tiling WA`;
  const description = loc.meta || `Professional tiling in ${loc.name}, Perth — ceramic, porcelain, mosaic, large-format and natural stone for bathrooms, kitchens, outdoor areas, feature walls and commercial fit-outs. Free quotes. Call ${CONTACT.phone}.`;
  const keywords = `tiling ${loc.name}, tilers ${loc.name}, bathroom tiling ${loc.name}, kitchen splashback ${loc.name}, ${loc.name} tiler, tiling Perth`;

  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Areas We Serve", path: "/areas" },
    { name: loc.name, path: `/tiling/${suburb}` },
  ];

  const faqs = loc.faqs || [];
  const highlights = loc.highlights || [];
  const intro = loc.intro || [];
  const featured = (loc.featured || [])
    .map((f) => ({ ...f, cat: categories.find((c) => c.id === f.slug) }))
    .filter((f) => f.cat);

  return (
    <SmoothScroll>
      <Seo
        pageKey="home"
        overrideTitle={title}
        overrideDescription={description}
        overrideKeywords={keywords}
        service={{ name: `Tiling in ${loc.name}` }}
        faqs={faqs}
        breadcrumb={breadcrumb}
      />
      <div className="grain-overlay" aria-hidden />
      <div data-testid={`location-page-${suburb}`} className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 border-b border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <Link to="/areas" className="hover:text-[#9c7a4d] transition-colors">Areas</Link>
              <span>/</span>
              <span className="text-[#0f172a]">{loc.name}</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> Servicing {loc.name}, Western Australia
            </p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <Reveal load>Tiling in</Reveal>
              <Reveal load delay={0.12}>{loc.name}.</Reveal>
            </h1>
            <FadeUp delay={0.2}>
              {intro.map((p, i) => (
                <p key={i} className={`max-w-2xl text-[#475569] text-lg md:text-xl font-light leading-relaxed ${i === 0 ? "mt-8" : "mt-5"}`}>
                  {p}
                </p>
              ))}
            </FadeUp>
            <FadeUp delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="/#enquire" data-testid="location-enquire-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  Get a Free Quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Popular in this suburb — featured services with local angles */}
        {featured.length > 0 && (
          <section className="py-20 md:py-28" data-testid="location-featured">
            <div className="mx-auto max-w-[1600px] px-6 md:px-12">
              <p className="overline !text-[#9c7a4d] mb-6">Popular in {loc.name}</p>
              <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-12">
                <Reveal>What {loc.name}</Reveal>
                <Reveal delay={0.08}>asks us for most.</Reveal>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {featured.map((f, i) => (
                  <motion.div
                    key={f.slug}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-6%" }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link to={`/services/${f.slug}`} data-testid={`location-featured-${f.slug}`} className="group relative overflow-hidden aspect-[4/5] border border-[#0f172a]/10 block">
                      <img src={f.cat.image} alt={`${f.cat.title} in ${loc.name}`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover brightness-[0.55] group-hover:brightness-75 transition-[filter,transform] duration-700 group-hover:scale-105" />
                      <div className="relative z-10 h-full flex flex-col justify-end p-6">
                        <h3 className="font-serif text-2xl text-white flex items-center gap-2">
                          {f.cat.title}
                          <ArrowRight className="h-4 w-4 text-[#c9a87c] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                        </h3>
                        <p className="mt-3 text-white/80 text-sm font-light leading-relaxed">{f.line}</p>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Why local */}
        <section className="py-16 md:py-24 bg-[#f1f5f9] border-y border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-4xl leading-tight">
              Why {loc.name} homeowners choose Perth Tiling WA
            </h2>
            <ul className="space-y-4">
              {highlights.map((item) => (
                <li key={item} className="flex items-start gap-3 border-b border-[#0f172a]/10 pb-4">
                  <Check className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-0.5" />
                  <span className="text-[#1e293b] font-light text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* All services in this suburb */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6">Our Services in {loc.name}</p>
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-12">
              <Reveal>Every discipline,</Reveal>
              <Reveal delay={0.08}>delivered locally.</Reveal>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {categories.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-6%" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link to={`/services/${c.id}`} data-testid={`location-service-${c.id}`} className="group relative overflow-hidden aspect-[16/11] border border-[#0f172a]/10 block">
                    <img src={c.image} alt={`${c.title} in ${loc.name}`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover brightness-[0.55] group-hover:brightness-75 transition-[filter,transform] duration-700 group-hover:scale-105" />
                    <div className="relative z-10 h-full flex flex-col justify-end p-6">
                      <h3 className="font-serif text-2xl text-white flex items-center gap-2">
                        {c.title}
                        <ArrowRight className="h-4 w-4 text-[#c9a87c] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                      </h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 md:py-28 bg-[#f1f5f9] border-y border-[#0f172a]/10" data-testid="location-faqs">
          <div className="mx-auto max-w-[1000px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6">FAQs</p>
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-12">
              <Reveal>Tiling in {loc.name},</Reveal>
              <Reveal delay={0.08}>answered.</Reveal>
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[#0f172a]/10" data-testid={`location-faq-${i}`}>
                  <AccordionTrigger className="text-left text-[#0f172a] font-serif text-xl md:text-2xl font-light hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748b] font-light text-base md:text-lg leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Other areas */}
        <section className="py-20 md:py-24" data-testid="other-areas">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <p className="overline !text-[#94a3b8]">Other Areas We Serve</p>
              <Link to="/areas" data-testid="location-all-areas-link" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                View all areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {SUBURBS.filter((s) => s.slug !== suburb).map((s) => (
                <Link key={s.slug} to={`/tiling/${s.slug}`} className="text-sm text-[#475569] hover:text-white hover:bg-[#9c7a4d] border border-[#0f172a]/15 hover:border-[#9c7a4d] px-4 py-2 transition-colors">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
